import {
    CalendarRange,
    ChevronLeft,
    ChevronRight,
    X,
} from "lucide-react";
import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import type {
    DateRangePickerProps,
    DateRangeValue,
} from "./DateRangePicker.types";

import "./DateRangePicker.css";

const WEEK_DAYS =
    [
        "Su",
        "Mo",
        "Tu",
        "We",
        "Th",
        "Fr",
        "Sa",
    ];

const EMPTY_RANGE: DateRangeValue =
{
    from: "",
    to: "",
};

const pad =
    (
        value: number
    ) =>
        String(
            value
        ).padStart(
            2,
            "0"
        );

const toDateKey =
    (
        date: Date
    ) =>
        `${date.getFullYear()}-${pad(
            date.getMonth() +
            1
        )}-${pad(
            date.getDate()
        )}`;

const parseDate =
    (
        value: string
    ) => {
        if (
            !value
        ) {
            return null;
        }

        const [
            year,
            month,
            day,
        ] =
            value
                .split("-")
                .map(
                    Number
                );

        if (
            !year ||
            !month ||
            !day
        ) {
            return null;
        }

        return new Date(
            year,
            month -
            1,
            day
        );
    };

const startOfMonth =
    (
        date: Date
    ) =>
        new Date(
            date.getFullYear(),
            date.getMonth(),
            1
        );

const addMonths =
    (
        date: Date,
        amount: number
    ) =>
        new Date(
            date.getFullYear(),
            date.getMonth() +
            amount,
            1
        );

const formatDisplayDate =
    (
        value: string
    ) => {
        const date =
            parseDate(
                value
            );

        if (
            !date
        ) {
            return "";
        }

        return new Intl.DateTimeFormat(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
        ).format(
            date
        );
    };

const getMonthCells =
    (
        month: Date
    ) => {
        const year =
            month.getFullYear();

        const monthIndex =
            month.getMonth();

        const firstWeekDay =
            new Date(
                year,
                monthIndex,
                1
            ).getDay();

        const daysInMonth =
            new Date(
                year,
                monthIndex +
                1,
                0
            ).getDate();

        const cells:
            Array<
                Date | null
            > =
            [];

        for (
            let index =
                0;
            index <
            firstWeekDay;
            index +=
            1
        ) {
            cells.push(
                null
            );
        }

        for (
            let day =
                1;
            day <=
            daysInMonth;
            day +=
            1
        ) {
            cells.push(
                new Date(
                    year,
                    monthIndex,
                    day
                )
            );
        }

        while (
            cells.length %
            7 !==
            0
        ) {
            cells.push(
                null
            );
        }

        return cells;
    };

const DateRangePicker =
    ({
        value,
        onChange,
        placeholder =
        "Select custom date range",
        monthsToShow =
        2,
        minDate,
        maxDate,
        disabled =
        false,
        clearable =
        true,
        className =
        "",
        ariaLabel =
        "Select date range",
    }: DateRangePickerProps) => {
        const rootRef =
            useRef<HTMLDivElement>(
                null
            );

        const [
            open,
            setOpen,
        ] =
            useState(
                false
            );

        const [
            draft,
            setDraft,
        ] =
            useState<DateRangeValue>(
                value
            );

        const initialMonth =
            useMemo(
                () =>
                    startOfMonth(
                        parseDate(
                            value.from
                        ) ??
                        new Date()
                    ),
                [
                    value.from,
                ]
            );

        const [
            firstMonth,
            setFirstMonth,
        ] =
            useState(
                initialMonth
            );

        useEffect(
            () => {
                if (
                    !open
                ) {
                    return;
                }

                setDraft(
                    value
                );

                setFirstMonth(
                    initialMonth
                );
            },
            [
                open,
                value,
                initialMonth,
            ]
        );

        useEffect(
            () => {
                const handleOutsideClick =
                    (
                        event: MouseEvent
                    ) => {
                        if (
                            !rootRef.current?.contains(
                                event.target as Node
                            )
                        ) {
                            setOpen(
                                false
                            );
                        }
                    };

                const handleEscape =
                    (
                        event: KeyboardEvent
                    ) => {
                        if (
                            event.key ===
                            "Escape"
                        ) {
                            setOpen(
                                false
                            );
                        }
                    };

                document.addEventListener(
                    "mousedown",
                    handleOutsideClick
                );

                window.addEventListener(
                    "keydown",
                    handleEscape
                );

                return () => {
                    document.removeEventListener(
                        "mousedown",
                        handleOutsideClick
                    );

                    window.removeEventListener(
                        "keydown",
                        handleEscape
                    );
                };
            },
            []
        );

        const months =
            useMemo(
                () =>
                    Array.from(
                        {
                            length:
                                monthsToShow,
                        },
                        (
                            _,
                            index
                        ) =>
                            addMonths(
                                firstMonth,
                                index
                            )
                    ),
                [
                    firstMonth,
                    monthsToShow,
                ]
            );

        const hasValue =
            Boolean(
                value.from ||
                value.to
            );

        const label =
            value.from
                ? value.to
                    ? `${formatDisplayDate(
                        value.from
                    )} — ${formatDisplayDate(
                        value.to
                    )}`
                    : formatDisplayDate(
                        value.from
                    )
                : placeholder;

        const isDisabledDate =
            (
                key: string
            ) =>
                Boolean(
                    (
                        minDate &&
                        key <
                        minDate
                    ) ||
                    (
                        maxDate &&
                        key >
                        maxDate
                    )
                );

        const handleDayClick =
            (
                date: Date
            ) => {
                const key =
                    toDateKey(
                        date
                    );

                if (
                    isDisabledDate(
                        key
                    )
                ) {
                    return;
                }

                if (
                    !draft.from ||
                    draft.to
                ) {
                    setDraft({
                        from: key,
                        to: "",
                    });

                    return;
                }

                if (
                    key <
                    draft.from
                ) {
                    setDraft({
                        from: key,
                        to:
                            draft.from,
                    });

                    return;
                }

                setDraft({
                    from:
                        draft.from,
                    to: key,
                });
            };

        const clearRange =
            () => {
                setDraft(
                    EMPTY_RANGE
                );

                onChange(
                    EMPTY_RANGE
                );

                setOpen(
                    false
                );
            };

        const applyRange =
            () => {
                if (
                    !draft.from
                ) {
                    clearRange();
                    return;
                }

                const nextValue =
                {
                    from:
                        draft.from,
                    to:
                        draft.to ||
                        draft.from,
                };

                onChange(
                    nextValue
                );

                setOpen(
                    false
                );
            };

        const renderMonth =
            (
                month: Date
            ) => {
                const monthTitle =
                    new Intl.DateTimeFormat(
                        "en-IN",
                        {
                            month:
                                "long",
                            year:
                                "numeric",
                        }
                    ).format(
                        month
                    );

                const today =
                    toDateKey(
                        new Date()
                    );

                return (
                    <div
                        className="global-date-range-month"
                        key={
                            monthTitle
                        }
                    >
                        <strong className="global-date-range-month-title">
                            {
                                monthTitle
                            }
                        </strong>

                        <div className="global-date-range-week">
                            {WEEK_DAYS.map(
                                (
                                    day
                                ) => (
                                    <span
                                        key={
                                            day
                                        }
                                    >
                                        {
                                            day
                                        }
                                    </span>
                                )
                            )}
                        </div>

                        <div className="global-date-range-grid">
                            {getMonthCells(
                                month
                            ).map(
                                (
                                    date,
                                    index
                                ) => {
                                    if (
                                        !date
                                    ) {
                                        return (
                                            <span
                                                key={`empty-${monthTitle}-${index}`}
                                                className="global-date-range-empty-cell"
                                            />
                                        );
                                    }

                                    const key =
                                        toDateKey(
                                            date
                                        );

                                    const isStart =
                                        draft.from ===
                                        key;

                                    const isEnd =
                                        draft.to ===
                                        key;

                                    const isInside =
                                        Boolean(
                                            draft.from &&
                                            draft.to &&
                                            key >
                                            draft.from &&
                                            key <
                                            draft.to
                                        );

                                    const isToday =
                                        key ===
                                        today;

                                    const dayDisabled =
                                        isDisabledDate(
                                            key
                                        );

                                    return (
                                        <button
                                            type="button"
                                            key={
                                                key
                                            }
                                            className={`global-date-range-day ${isInside
                                                ? "in-range"
                                                : ""
                                                } ${isStart
                                                    ? "range-start"
                                                    : ""
                                                } ${isEnd
                                                    ? "range-end"
                                                    : ""
                                                } ${isToday
                                                    ? "today"
                                                    : ""
                                                }`}
                                            disabled={
                                                dayDisabled
                                            }
                                            onClick={() =>
                                                handleDayClick(
                                                    date
                                                )
                                            }
                                            aria-pressed={
                                                isStart ||
                                                isEnd
                                            }
                                        >
                                            {
                                                date.getDate()
                                            }
                                        </button>
                                    );
                                }
                            )}
                        </div>
                    </div>
                );
            };

        return (
            <div
                className={`global-date-range-picker ${className}`.trim()}
                ref={
                    rootRef
                }
            >
                <div className="global-date-range-control">
                    <button
                        type="button"
                        className={`global-date-range-trigger ${open
                            ? "active"
                            : ""
                            }`}
                        disabled={
                            disabled
                        }
                        onClick={() =>
                            setOpen(
                                (
                                    previous
                                ) =>
                                    !previous
                            )
                        }
                        aria-label={
                            ariaLabel
                        }
                        aria-expanded={
                            open
                        }
                    >
                        <CalendarRange
                            size={
                                17
                            }
                            strokeWidth={
                                2
                            }
                        />

                        <span>
                            {
                                label
                            }
                        </span>
                    </button>

                    {clearable &&
                        hasValue &&
                        !disabled && (
                            <button
                                type="button"
                                className="global-date-range-clear"
                                onClick={
                                    clearRange
                                }
                                aria-label="Clear date range"
                                title="Clear date range"
                            >
                                <X
                                    size={
                                        15
                                    }
                                    strokeWidth={
                                        2.2
                                    }
                                />
                            </button>
                        )}
                </div>

                {open &&
                    !disabled && (
                        <div
                            className="global-date-range-popover"
                            role="dialog"
                            aria-label="Choose start and end dates"
                        >
                            <div className="global-date-range-popover-head">
                                <div>
                                    <strong>
                                        Custom date range
                                    </strong>

                                    <span>
                                        Select a start date and an end date.
                                    </span>
                                </div>

                                <div className="global-date-range-nav">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setFirstMonth(
                                                (
                                                    previous
                                                ) =>
                                                    addMonths(
                                                        previous,
                                                        -1
                                                    )
                                            )
                                        }
                                        aria-label="Previous month"
                                    >
                                        <ChevronLeft
                                            size={
                                                17
                                            }
                                            strokeWidth={
                                                2
                                            }
                                        />
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setFirstMonth(
                                                (
                                                    previous
                                                ) =>
                                                    addMonths(
                                                        previous,
                                                        1
                                                    )
                                            )
                                        }
                                        aria-label="Next month"
                                    >
                                        <ChevronRight
                                            size={
                                                17
                                            }
                                            strokeWidth={
                                                2
                                            }
                                        />
                                    </button>
                                </div>
                            </div>

                            <div
                                className={`global-date-range-calendars months-${monthsToShow}`}
                            >
                                {
                                    months.map(
                                        renderMonth
                                    )
                                }
                            </div>

                            <div className="global-date-range-selection">
                                <div>
                                    <span>
                                        From
                                    </span>

                                    <strong>
                                        {draft.from
                                            ? formatDisplayDate(
                                                draft.from
                                            )
                                            : "Select date"}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        To
                                    </span>

                                    <strong>
                                        {draft.to
                                            ? formatDisplayDate(
                                                draft.to
                                            )
                                            : "Select date"}
                                    </strong>
                                </div>
                            </div>

                            <div className="global-date-range-actions">
                                <button
                                    type="button"
                                    className="global-date-range-reset"
                                    onClick={
                                        clearRange
                                    }
                                >
                                    Clear
                                </button>

                                <button
                                    type="button"
                                    className="global-date-range-apply"
                                    onClick={
                                        applyRange
                                    }
                                >
                                    Apply range
                                </button>
                            </div>
                        </div>
                    )}
            </div>
        );
    };

export default DateRangePicker;
